import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { usePageMeta } from "../usePageMeta";
import AppLayout from "../layout";
import AppFilters from "../components/AppFilters";
import DetailsCards from "../components/DetailsCards";
import AdsCards from "../components/AdsCards";
import AppAnalytics from "../components/Analytics";
import { fetcher } from "../services/api";

const getToday = () => moment().tz("Asia/Tbilisi").format("YYYY-MM-DD");

type AnalyticDataType = {
  avg_cpm: number;
  ctr_rate: number;
  fill_rate: number;
  total_impressions: number;
  total_received: number;
  total_requested: number;
  total_revenue: number;
};

export type ChartMetrics = {
  display_name: string;
  total_revenue: number;
  total_impressions: number;
  avg_cpm: number;
  total_requested: number;
  total_received: number;
  fill_rate: number;
  ctr_rate: number;
};

type ClientsDataType = {
  client_id: number;
  display_name: string;
  total_download: string;
  total_search: string;
  total_share: string;
  total_trending: string;
  total_view: string;
};

type FiltersType = {
  types: number[];
  clients: number[];
  countries: string[];
  date_from: string;
  date_to: string;
};

export default function HomePage() {
  const accessToken = Cookies.get("access_token");
  const [isAnalyticalView, setIsAnalyticalView] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<FiltersType>({
    types: [],
    clients: [],
    countries: [],
    date_from: getToday(),
    date_to: getToday(),
  });
  const [analyticData, setAnalyticData] = useState<AnalyticDataType | null>(
    null
  );
  const [clientsData, setClientsData] = useState<ClientsDataType[] | null>(
    null
  );
  const [chartData, setChartData] = useState<ChartMetrics[] | null>(null);
  const [dateRangeMode, setDateRangeMode] = useState<
    "Day" | "Week" | "Month" | "Custom"
  >("Day");
  const previousQueryRef = useRef<string | null>(null);
  const hasFetchedOnce = useRef(false);

  const allFiltersReady = useMemo(() => {
    const { types, clients, countries } = selectedFilters;
    return types.length && clients.length && countries.length;
  }, [selectedFilters]);

  const queryString = useMemo(() => {
    const { types, clients, countries, date_from, date_to } = selectedFilters;
    return new URLSearchParams({
      types: types.join(","),
      clients: clients.join(","),
      countries: countries.join(","),
      date_from,
      date_to,
    }).toString();
  }, [selectedFilters]);

  const fetchAnalyticsAndClients = useCallback(async () => {
    if (!allFiltersReady || queryString === previousQueryRef.current) return;

    previousQueryRef.current = queryString;

    try {
      const [analyticsRes, clientsRes] = await Promise.all([
        fetcher<{ data: AnalyticDataType }>(
          `/publisher/analytic?${queryString}`
        ),
        fetcher<{ data: ClientsDataType[] }>(
          `/publisher/analytic/clients_data?${queryString}`
        ),
      ]);

      setAnalyticData(analyticsRes?.data?.data || null);
      setClientsData(clientsRes?.data?.data || []);
      if (!hasFetchedOnce.current) {
        hasFetchedOnce.current = true;
        setIsAnalyticalView(true);
      }
    } catch (error) {
      console.error("Error fetching analytics/clients:", error);
    }
  }, [allFiltersReady, queryString]);

  useEffect(() => {
    if (dateRangeMode === "Custom") {
      setIsAnalyticalView(false);
    }
  }, [dateRangeMode]);

  const fetchChartData = useCallback(async () => {
    if (!allFiltersReady || dateRangeMode === "Custom") return;

    try {
      const res = await fetcher<{ data: ChartMetrics[] }>(
        `/publisher/analytic/chart?${queryString}`
      );
      setChartData(res?.data?.data || null);
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  }, [allFiltersReady, dateRangeMode, queryString]);

  useEffect(() => {
    fetchAnalyticsAndClients();
    if (dateRangeMode !== "Custom" && isAnalyticalView) fetchChartData();
  }, [
    dateRangeMode,
    fetchAnalyticsAndClients,
    fetchChartData,
    isAnalyticalView,
  ]);

  usePageMeta({
    title: "KLIPY Partner Dashboard",
    description:
      "Get full control on GIF API with KLIPY partner dashboard: integrate and manage viral content and adjust everything to match your audience.",
    ogTitle: "KLIPY Partner Dashboard",
    ogDescription:
      "Get full control on GIF API with KLIPY partner dashboard: integrate and manage viral content and adjust everything to match your audience.",
    ogImage: "https://partner.klipy.com/images/klipy-api-banner-v2.png",
    ogUrl: "https://partner.klipy.com",
    canonicalUrl: "https://partner.klipy.com",

    twitterTitle: "KLIPY Partner Dashboard",
    twitterDescription:
      "Get full control on GIF API with KLIPY partner dashboard: integrate and manage viral content and adjust everything to match your audience.",
    twitterImage: "https://partner.klipy.com/images/klipy-api-banner-v2.png",
  });

  useEffect(() => {
    if (accessToken) {
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + window.location.search
      );
    }
  }, [accessToken]);

  return (
    <AppLayout>
      <AppFilters
        isAnalyticalView={isAnalyticalView}
        setIsAnalyticalView={setIsAnalyticalView}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        dateRangeMode={dateRangeMode}
        setDateRangeMode={setDateRangeMode}
      />
      <DetailsCards
        isAnalyticalView={isAnalyticalView}
        analyticData={analyticData}
        chartData={chartData}
        dateRangeMode={dateRangeMode}
      />
      <AdsCards
        isAnalyticalView={isAnalyticalView}
        analyticData={analyticData}
        chartData={chartData}
        dateRangeMode={dateRangeMode}
      />
      <AppAnalytics clientsData={clientsData} />
    </AppLayout>
  );
}
